import {
  TypeImplementation,
  withSystem,
  Plugins,
  EnvTypes,
} from '@packages/systems/wfdl';
import {functionOf, TTypeConstructor} from '@packages/systems/wfdl-ast';
import {qualify, typeName, iconName} from '@packages/systems/wfdl-Qualified';
import {type DynamoEnvValueType} from '../types';
import {TOption} from '@packages/systems/plugins/Option';

import {
  TDynamoRichText,
  getDynamoRichTextToRichTextChildrenImpl,
} from '@packages/systems/plugins-utils/DynamoGateway';
import {TPredicate, FALSE, TRUE} from '@packages/systems/plugins-utils/Boolean';
import {operand} from '@packages/systems/plugins/ConditionsOperand';

export const RichTextType = TypeImplementation.Custom({
  render: () => null,
  icon: TypeImplementation.IconByName(
    qualify(Plugins.name('Basic'), iconName('RichText'))
  ),
});

const TRichTextChildren = TTypeConstructor(
  qualify(Plugins.name('Basic'), typeName('RichTextChildren'))
);

const emptyArray: [] = [];
const alwaysEmptyArray = () => emptyArray;

export const dynamoRichTextToRichTextChildren = {
  envType: {
    dataType: functionOf(
      withSystem({isBindable: true}, TOption(TDynamoRichText)),
      TRichTextChildren
    ),
    exported: 'visible' as const,
    implicit: 'prefilledArgs' as const,
  },
  value: getDynamoRichTextToRichTextChildrenImpl(alwaysEmptyArray),
};

export const dynamoRichTextOperand = operand(TOption(TDynamoRichText));

const isRichTextValueSet = (
  dynamoValue: DynamoEnvValueType<unknown>
): boolean => {
  if (dynamoValue === null || dynamoValue.tag === 'None') {
    return false;
  }

  const richTextValue = dynamoValue.val;

  if (typeof richTextValue === 'string') {
    return true;
  }

  if (Array.isArray(richTextValue)) {
    return richTextValue.length > 0;
  }

  return false;
};

export const dynamoRichTextIsSet: EnvTypes.TypedValue = {
  envType: {
    exported: 'visible',
    dataType: withSystem(
      {label: 'is set'},
      TPredicate(TOption(TDynamoRichText))
    ),
  },
  value: (dynamoValue: DynamoEnvValueType<unknown>) => {
    return isRichTextValueSet(dynamoValue) ? TRUE : FALSE;
  },
};

export const dynamoRichTextIsNotSet: EnvTypes.TypedValue = {
  envType: {
    exported: 'visible',
    dataType: withSystem(
      {label: 'is not set'},
      TPredicate(TOption(TDynamoRichText))
    ),
  },
  value: (dynamoValue: DynamoEnvValueType<unknown>) => {
    return isRichTextValueSet(dynamoValue) ? FALSE : TRUE;
  },
};
